import { Injectable, inject } from '@angular/core';
import { Functions, httpsCallableData } from '@angular/fire/functions';

import { AlertController, ModalController } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, EMPTY, Subscription, timer, firstValueFrom } from 'rxjs';
import { map, delay, expand, reduce } from 'rxjs/operators';
import { DateTime, Duration } from 'luxon';

import { PuzzleFrameworkService } from 'projects/tools/src/lib/puzzle-framework-module/puzzle-framework.service';
import { HelperFunctionsService } from 'projects/tools/src/lib/shared-module/helpers/helper-functions.service';
import { BadwordsService } from 'projects/tools/src/lib/shared-module/leaderboard/badwords.service';
import { PuzzleKeyboardService } from 'projects/tools/src/lib/shared-module/puzzle-softkeyboard/puzzle-keyboard.service';

import { LeaderboardEntryModel } from './leaderboard-entry.model';
import { PuzzleFrameworkConfigModel } from 'projects/tools/src/lib/store/puzzle-framework-config.model';
import { PuzzleGameModel } from 'projects/tools/src/lib/store/puzzle-game.model';
import { PuzzleGameState } from 'projects/tools/src/lib/store/puzzle-game.state';
import { PuzzleTimeRecordState } from 'projects/tools/src/lib/store/puzzle-time-record.state';
import { TimeRecordModel } from 'projects/tools/src/lib/store/time-record.model';
import {
  LetterDeleteRestrictions,
  LetterOverwriteRestrictions,
  WordJumpOnRestrictions,
  WordJumpToRestrictions,
} from 'projects/tools/src/lib/store/puzzle-game-state.model';
import { LeaderboardAssociationModel } from './store/leaderboard-association.model';
import { LeaderboardState } from './store/leaderboard.state';

import * as PuzzleGameActions from 'projects/tools/src/lib/store/puzzle-game.actions';
import * as LeaderboardActions from './store/leaderboard.actions';

import { LeaderboardReviewComponent } from 'projects/tools/src/lib/shared-module/leaderboard-review/leaderboard-review.component';

import { buildEnv } from 'build-environment';

const __PROJECT_NAME__: string = buildEnv.projectName;

// this helper is needed due to luxon not supporting locale based week starts,
// //and the start of the week in the other calculation is sunday, not monday
// it is now starting monday
function getWeekStart(date) {
  const offset = Duration.fromObject({ days: 0 });
  return date.plus(offset).startOf('week').minus(offset);
}

@Injectable({
  providedIn: 'root',
})
export class LeaderboardService {
  private functions: Functions = inject(Functions);

  private puzzleIDSubscription: Subscription;
  private puzzleConfigSubscription: Subscription;
  private puzzleGameSubscription: Subscription;
  private puzzleTimeSubscription: Subscription;
  private leaderboardStateSubscription: Subscription;
  private timerSubscription: Subscription;

  private puzzleFrameworkConfig: PuzzleFrameworkConfigModel;
  private puzzleGame: PuzzleGameModel;
  private puzzleTime: TimeRecordModel;
  private puzzleID: string;
  private configPuzzleName: string;
  public leaderboardAssociation: LeaderboardAssociationModel;

  private leaderboardRulesDialogVisible = false;
  private errorVisible = false;

  private entryId: string;

  private currentLeaderboardEntrySubject: BehaviorSubject<LeaderboardEntryModel> =
    new BehaviorSubject<LeaderboardEntryModel>(undefined);
  public readonly currentLeaderboardEntry$: Observable<LeaderboardEntryModel> =
    this.currentLeaderboardEntrySubject.asObservable();

  // distributes the current leaderboardContext
  private leaderboardContextSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
  public readonly leaderboardContext$: Observable<any> = this.leaderboardContextSubject.asObservable();

  // indicates if the puzzle was finished
  private puzzleIsFinishedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly puzzleIsFinished$: Observable<boolean> = this.puzzleIsFinishedSubject.asObservable();

  // indicates if there's a leaderboard entry for the currently selected puzzledid
  private puzzleHasLeaderboardEntrySubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly puzzleHasLeaderboardEntry$: Observable<boolean> =
    this.puzzleHasLeaderboardEntrySubject.asObservable();

  // indicates if leaderboard is enabled for this game at all
  private leaderboardEnabledSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public readonly leaderboardEnabled$: Observable<boolean> = this.leaderboardEnabledSubject.asObservable();

  // indicates if the highscore can still be submitted from a date/time point of view
  private withinLeaderboardSubmissionTimeSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public readonly withinLeaderboardSubmissionTime$: Observable<boolean> =
    this.withinLeaderboardSubmissionTimeSubject.asObservable();

  // FIXME: no use right now
  private leaderboardActiveSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public readonly leaderboardActive$: Observable<boolean> = this.leaderboardActiveSubject.asObservable();

  // FIXME: rename -> gameHasStrictRulesActive
  private strictRulesActiveSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly strictRulesActive$: Observable<boolean> = this.strictRulesActiveSubject.asObservable();

  private leaderboardSubmissionStartSubject: BehaviorSubject<DateTime> = new BehaviorSubject<DateTime>(undefined);
  public readonly leaderboardSubmissionStart$: Observable<any> = this.leaderboardSubmissionStartSubject.asObservable();
  public leaderboardSubmissionStart: DateTime = undefined;

  private leaderboardSubmissionEndSubject: BehaviorSubject<DateTime> = new BehaviorSubject<DateTime>(undefined);
  public readonly leaderboardSubmissionEnd$: Observable<any> = this.leaderboardSubmissionEndSubject.asObservable();
  public leaderboardSubmissionEnd: DateTime = undefined;

  private leaderboardSubmissionStartLocalizedStrSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public readonly leaderboardSubmissionStartLocalizedStr$: Observable<string> =
    this.leaderboardSubmissionStartLocalizedStrSubject.asObservable();

  private submissionPolicyViolationSubject: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
  public readonly submissionPolicyViolation$: Observable<string> = this.submissionPolicyViolationSubject.asObservable();

  private leaderboardSubmissionEndLocalizedStrSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public readonly leaderboardSubmissionEndLocalizedStr$: Observable<string> =
    this.leaderboardSubmissionEndLocalizedStrSubject.asObservable();

  private leaderboardContextFunction: (data: any) => Observable<any>;
  private leaderboardPageFunction: (data: any) => Observable<any>;
  private newLeaderboardEntryFunction: (data: any) => Observable<any>;
  private adminListDayCollections: (data: any) => Observable<any>;
  private adminListCollectionEntries: (data: any) => Observable<any>;
  private adminDeleteCollectionEntry: (data: any) => Observable<any>;
  private adminUpdateCollectionEntry: (data: any) => Observable<any>;
  private adminAddNicknameExemption: (data: any) => Observable<any>;
  private reportNicknameFunction: (data: any) => Observable<any>;

  private reviewModal: HTMLIonModalElement;
  private reviewModalIsActive = false;

  leaderboardContextResponse$: Observable<any> = EMPTY;

  public topN = 5;
  public lastN = 5;
  public contextN = 5;
  public resetToLeaderboardSettings = false;

  constructor(
    private keyboard: PuzzleKeyboardService,
    private store: Store,
    private framework: PuzzleFrameworkService,
    private alertController: AlertController,
    private translate: TranslateService,
    private helpers: HelperFunctionsService,
    private badwords: BadwordsService,
    private ionicModal: ModalController
  ) {
    this.leaderboardContextFunction = httpsCallableData(this.functions, 'leaderboardWithContext', { timeout: 40_000 });
    this.leaderboardPageFunction = httpsCallableData(this.functions, 'leaderboardPage', { timeout: 40_000 });
    this.newLeaderboardEntryFunction = httpsCallableData(this.functions, 'newLeaderboardEntry', { timeout: 40_000 });
    this.adminListDayCollections = httpsCallableData(this.functions, 'adminListDayCollections', { timeout: 40_000 });
    this.adminListCollectionEntries = httpsCallableData(this.functions, 'adminListCollectionEntries', {
      timeout: 40_000,
    });
    this.adminDeleteCollectionEntry = httpsCallableData(this.functions, 'adminDeleteCollectionEntry', {
      timeout: 40_000,
    });
    this.adminUpdateCollectionEntry = httpsCallableData(this.functions, 'adminUpdateCollectionEntry', {
      timeout: 40_000,
    });
    this.adminAddNicknameExemption = httpsCallableData(this.functions, 'adminAddNicknameExemption', {
      timeout: 40_000,
    });
    this.reportNicknameFunction = httpsCallableData(this.functions, 'reportNickname', {
      timeout: 40_000,
    });

    this.strictRulesActive$.subscribe((isActive) => {
      this.resetToLeaderboardSettings =
        isActive && this.leaderboardAssociation && this.leaderboardAssociation.wantLeaderboardRules;
    });

    this.puzzleIDSubscription = this.store
      .select((state) => state.puzzleID)
      .subscribe((puzzleID) => {
        if (this.puzzleID !== puzzleID) {
          this.entryId = undefined;
          this.puzzleID = puzzleID;

          this.leaderboardContextSubject.next(undefined);
          this.puzzleHasLeaderboardEntrySubject.next(false);
          if (this.leaderboardStateSubscription) {
            this.leaderboardStateSubscription.unsubscribe();
          }
          this.leaderboardStateSubscription = this.store
            .select(LeaderboardState.getByID(puzzleID))
            .subscribe((leaderboardAssociation: LeaderboardAssociationModel) => {
              this.leaderboardAssociation = leaderboardAssociation;
              if (this.leaderboardAssociation) {
                if (
                  (this.leaderboardAssociation.helpersUsed || []).length > 0 ||
                  !this.leaderboardAssociation.wantLeaderboardRules
                ) {
                  this.strictRulesActiveSubject.next(false);
                } else {
                  this.strictRulesActiveSubject.next(true);
                }
                this.puzzleIsFinishedSubject.next(leaderboardAssociation.puzzleIsFinished);
                this.entryId = leaderboardAssociation.leaderboardUUID;

                if (
                  this.framework.puzzleFrameworkConfig.leaderboardSubmissionPolicy === 'first-only' &&
                  leaderboardAssociation.startCounter > 1
                ) {
                  this.submissionPolicyViolationSubject.next('first-only');
                } else {
                  this.submissionPolicyViolationSubject.next(undefined);
                }

                return this.updateLeaderboardAssociation();
              }
            });
        }

        this.configPuzzleName = this.framework.configPuzzleName;

        if (this.puzzleID) {
          if (this.puzzleGameSubscription) {
            this.puzzleGameSubscription.unsubscribe();
          }

          this.puzzleGameSubscription = this.store
            .select(PuzzleGameState.getByID(puzzleID))
            .subscribe(async (puzzleGame: PuzzleGameModel) => {
              if (puzzleGame === null) {
                return;
              }
              this.puzzleGame = puzzleGame;
              if (puzzleGame.finished) {
                this.store.dispatch(
                  new LeaderboardActions.SetFinished({
                    projectId: __PROJECT_NAME__,
                    puzzleName: this.configPuzzleName,
                    puzzleId: this.puzzleID,
                  })
                );
              }
              return this.updateLeaderboardAssociation();
            });

          if (this.puzzleTimeSubscription) {
            this.puzzleTimeSubscription.unsubscribe();
          }

          this.puzzleTimeSubscription = this.store
            .select(PuzzleTimeRecordState.getTimeRecordByID)
            .pipe(map((filterFn) => filterFn(puzzleID)))
            .subscribe((puzzleTime) => {
              if (!puzzleTime) {
                return;
              }
              this.puzzleTime = puzzleTime.timeRecord;
            });
        }

        if (this.puzzleConfigSubscription) {
          this.puzzleConfigSubscription.unsubscribe();
        }

        this.puzzleConfigSubscription = this.store
          .select((state) => state.puzzleFrameworkConfig)
          .subscribe((puzzleFrameworkConfig) => {
            if (!puzzleFrameworkConfig) {
              return;
            }
            this.puzzleFrameworkConfig = puzzleFrameworkConfig;
            this.leaderboardEnabledSubject.next(this.puzzleFrameworkConfig.leaderboardEnabled || false);
            // Stop here if the leaderboard is not enabled
            // TODO: this is a bit of a hack, the service should not be started from te beginning
            //  Maybe ith this: https://stackoverflow.com/a/43450317/4396362
            //  But the service is used in multiple places and I'm too lazy now :-)
            if (!this.puzzleFrameworkConfig.leaderboardEnabled) {
              return;
            }
            this.setupLeaderboardSubscriptions();
            return this.updateLeaderboardAssociation();
          });
      });
  }

  public checkInitialSettings() {
    this.store
      .selectOnce((state) => state.puzzleGameState.letterOverwriteRestriction)
      .subscribe((letterOverwriteRestriction) => {
        if (
          !(
            letterOverwriteRestriction === LetterOverwriteRestrictions.All ||
            letterOverwriteRestriction === LetterOverwriteRestrictions.None
          )
        ) {
          void this.useHelper('highlight_right_and_wrong');
        }
      });

    this.store
      .selectOnce((state) => state.puzzleGameState.letterDeleteRestriction)
      .subscribe((letterDeleteRestriction) => {
        if (
          !(
            letterDeleteRestriction === LetterDeleteRestrictions.CompletedWords ||
            letterDeleteRestriction === LetterDeleteRestrictions.None
          )
        ) {
          void this.useHelper('highlight_right_and_wrong');
        }
      });

    this.store
      .selectOnce((state) => state.puzzleGameState.wordJumpOnRestriction)
      .subscribe((wordJumpOnRestriction) => {
        if (wordJumpOnRestriction === WordJumpOnRestrictions.CorrectWord) {
          void this.useHelper('highlight_right_and_wrong');
        }
      });

    this.store
      .selectOnce((state) => state.puzzleGameState.wordJumpToRestriction)
      .subscribe((wordJumpToRestriction) => {
        if (wordJumpToRestriction === WordJumpToRestrictions.IncorrectWord) {
          void this.useHelper('highlight_right_and_wrong');
        }
      });

    this.store
      .selectOnce((state) => state.puzzleGameState.highlightRightAndWrongEnabled)
      .subscribe((highlightRightAndWrongEnabled) => {
        if (highlightRightAndWrongEnabled !== false) {
          void this.useHelper('highlight_right_and_wrong');
        }
      });

    this.store
      .selectOnce((state) => state.puzzleGameState.showErrorsImmediately)
      .subscribe((showErrorsImmediately: boolean) => {
        if (showErrorsImmediately !== false) {
          void this.useHelper('highlight_right_and_wrong');
        }
      });
  }

  public updateLeaderboardAssociation() {
    if (
      this.leaderboardAssociation &&
      this.leaderboardAssociation.submittedEntryUUID &&
      this.puzzleID &&
      this.configPuzzleName
    ) {
      this.currentLeaderboardEntrySubject.next(this.leaderboardAssociation.submittedEntry);
      return this.getLeaderboardContext(
        this.leaderboardAssociation.submittedEntryUUID,
        __PROJECT_NAME__,
        this.configPuzzleName,
        this.puzzleID,
        this.leaderboardAssociation.submittedEntry?.helpersUsed.length === 0,
        this.leaderboardSubmissionStart,
        this.leaderboardSubmissionEnd
      );
    } else {
      this.currentLeaderboardEntrySubject.next(undefined);
      return Promise.resolve();
    }
  }

  public humanDate(luxonDate) {
    const currentLang = (this.translate.currentLang || 'de-').indexOf('de-') === 0 ? 'de-DE' : 'en-US';
    return luxonDate
      .setLocale(currentLang)
      .toLocaleString({ weekday: 'short', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  private setupLeaderboardSubscriptions() {
    this.leaderboardSubmissionStart = this.getPuzzleLeaderboardSubmissionStartDate();

    if (this.leaderboardSubmissionStart.isValid) {
      this.leaderboardSubmissionStartSubject.next(this.leaderboardSubmissionStart);
      this.leaderboardSubmissionStartLocalizedStrSubject.next(this.humanDate(this.leaderboardSubmissionStart));
    }

    this.leaderboardSubmissionEnd = this.getPuzzleLeaderboardSubmissionEndDate();

    if (this.leaderboardSubmissionEnd.isValid) {
      this.leaderboardSubmissionEndSubject.next(this.leaderboardSubmissionEnd);

      this.leaderboardSubmissionEndLocalizedStrSubject.next(this.humanDate(this.leaderboardSubmissionEnd));

      if (this.timerSubscription) {
        this.timerSubscription.unsubscribe();
      }

      if (this.leaderboardSubmissionEnd + Duration.fromMillis(300) < DateTime.now()) {
        void this.timeoutLeaderboardSubmission();
      } else if (this.leaderboardSubmissionEnd > DateTime.now()) {
        this.withinLeaderboardSubmissionTimeSubject.next(true);
        this.timerSubscription = timer(this.leaderboardSubmissionEnd.toJSDate()).subscribe((_) => {
          void this.timeoutLeaderboardSubmission(true);
        });
      }
    }
  }

  private async timeoutLeaderboardSubmission(showAlert: boolean = false) {
    this.withinLeaderboardSubmissionTimeSubject.next(false);
    if (showAlert) {
      const translated = await Promise.all([
        firstValueFrom(this.translate.get(`PUZZLE.leaderboard_service.timeout.title`)),
        firstValueFrom(this.translate.get(`PUZZLE.leaderboard_service.timeout.ok`)),
        firstValueFrom(this.translate.get(`PUZZLE.leaderboard_service.timeout.message`)),
      ]);

      const alert = await this.alertController.create({
        header: translated[0],
        message: translated[2],
        backdropDismiss: true,
        cssClass: 'puzzle-alert',
        buttons: [
          {
            text: translated[1],
            cssClass: 'primary',
            role: 'continue',
          },
        ],
      });

      await alert.present();

      const result = await alert.onDidDismiss();

      return false;
    }
  }

  public async getLeaderboardPage(pageOffset: number, pageSize: number, strictRules: boolean) {
    return firstValueFrom(
      this.leaderboardPageFunction({
        projectId: __PROJECT_NAME__,
        puzzleName: this.configPuzzleName,
        puzzleId: this.puzzleID,
        strictRules,
        submissionStartDate: this.leaderboardSubmissionStart.toUTC().toJSON(),
        submissionEndDate: this.leaderboardSubmissionEnd.toUTC().toJSON(),
        _pageOffset: pageOffset,
        _pageSize: pageSize,
      })
    );
  }

  private async getLeaderboardContext(
    entryId: string,
    projectId: string,
    puzzleName: string,
    puzzleId: string,
    strictRules: boolean,
    submissionStart: DateTime,
    submissionEnd: DateTime
  ) {
    if (this.leaderboardContextResponse$ === EMPTY) {
      this.leaderboardContextResponse$ = this.leaderboardContextFunction({
        entryId,
        projectId,
        puzzleName,
        puzzleId,
        strictRules,
        submissionStartDate: submissionStart.toUTC().toJSON(),
        submissionEndDate: submissionEnd.toUTC().toJSON(),
        _topLimit: this.topN,
        _contextLimit: this.contextN,
        _lastLimit: this.lastN,
      });
      await firstValueFrom(this.leaderboardContextResponse$).then((res) => {
        this.leaderboardContextSubject.next(res);
        if (res && res.entry) {
          this.puzzleHasLeaderboardEntrySubject.next(true);
        }
        this.leaderboardContextResponse$ = EMPTY;
      });
    } else {
      await this.leaderboardContextResponse$;
    }
  }

  public getAdminCollections(password: string) {
    return this.adminListDayCollections({
      projectId: __PROJECT_NAME__,
      puzzleName: this.configPuzzleName,
      password,
    }).pipe(map((result) => result.collections || []));
  }

  public getAdminCollectionEntries(password: string, collectionId: string) {
    let _pageOffset = 0;
    let _pageSize = 100;
    return this.adminListCollectionEntries({
      projectId: __PROJECT_NAME__,
      puzzleName: this.configPuzzleName,
      collectionId,
      password,
      _pageOffset,
      _pageSize,
    }).pipe(
      expand((res: any[]) => {
        if (res['entries'].length === 0) {
          return EMPTY;
        } else {
          _pageOffset += _pageSize;
          return this.adminListCollectionEntries({
            projectId: __PROJECT_NAME__,
            puzzleName: this.configPuzzleName,
            collectionId,
            password,
            _pageOffset,
            _pageSize,
          });
        }
      }),
      reduce((acc, val) => {
        acc = [...acc, ...val.entries];
        return acc;
      }, [])
    );
  }

  public updateCollectionEntry(password: string, collectionId: string, entryId, entry) {
    return this.adminUpdateCollectionEntry({
      password,
      projectId: __PROJECT_NAME__,
      puzzleName: this.configPuzzleName,
      collectionId,
      entryId,
      entry,
    }).pipe(delay(500));
  }

  public addNicknameExemption(password: string, entry, isProfane) {
    return this.adminAddNicknameExemption({
      password,
      projectId: __PROJECT_NAME__,
      entry,
      isProfane,
    });
  }

  public reportNickname(nickname: string, entry?, report_reason?: string, confirmation1?: string) {
    return this.reportNicknameFunction({
      projectId: __PROJECT_NAME__,
      nickname,
      entry: entry || {},
      report_reason,
      confirmation1,
    });
  }

  private async setLeaderboardEntry(
    entryId: string,
    projectId: string,
    puzzleName: string,
    puzzleId: string,
    timePlayed: number,
    nickname: string,
    helpersUsed: string[],
    strictRules: boolean,
    profanity: boolean,
    profanityChecks: string[],
    submissionStart: DateTime,
    submissionEnd: DateTime
  ) {
    return await firstValueFrom(
      this.newLeaderboardEntryFunction({
        entryId,
        projectId,
        puzzleName,
        puzzleId,
        submissionStartDate: submissionStart.toUTC().toJSON(),
        submissionEndDate: submissionEnd.toUTC().toJSON(),
        entry: {
          timePlayed,
          nickname,
          helpersUsed,
          strictRules,
          profanity,
          profanityChecks,
        },
      })
    );
  }

  public async newLeaderboardEntry(name) {
    const profanityChecks = [];

    if (this.badwords.fuzzyCheck(name)) {
      profanityChecks.push('fuzzy');
    }

    if (this.badwords.phoneticsCheck(name)) {
      profanityChecks.push('phonetics');
    }

    try {
      const entry = await this.setLeaderboardEntry(
        this.entryId,
        __PROJECT_NAME__,
        this.configPuzzleName,
        this.puzzleID,
        this.puzzleTime.gameTime,
        name,
        this.leaderboardAssociation.helpersUsed,
        this.leaderboardAssociation.wantLeaderboardRules,
        profanityChecks.length > 0 && true,
        profanityChecks,
        this.leaderboardSubmissionStart,
        this.leaderboardSubmissionEnd
      );

      if (entry) {
        this.store.dispatch(
          new LeaderboardActions.SavePostedEntry({
            projectId: __PROJECT_NAME__,
            puzzleName: this.configPuzzleName,
            puzzleId: this.puzzleID,
            submittedEntry: entry.entry,
            submittedEntryUUID: this.entryId,
          })
        );
      }
      await this.getLeaderboardContext(
        this.entryId,
        __PROJECT_NAME__,
        this.configPuzzleName,
        this.puzzleID,
        this.leaderboardAssociation.helpersUsed.length === 0,
        this.leaderboardSubmissionStart,
        this.leaderboardSubmissionEnd
      );
    } catch (error) {
      await this.alertError(error);
    }
  }

  public async initLeaderboardGame(wantLeaderboardRules: boolean) {
    if (wantLeaderboardRules) {
      await this.resetSettingsToLeaderboardRules();
    }
    this.store.dispatch(
      new LeaderboardActions.InitLeaderboardAssociation({
        projectId: __PROJECT_NAME__,
        puzzleName: this.configPuzzleName,
        puzzleId: this.puzzleID,
        wantLeaderboardRules,
      })
    );
  }

  public async useHelper(usedHelper: string) {
    // do not add used helpers after the game is finished
    if (this.puzzleGame.finished) {
      return;
    }

    this.store.dispatch(
      new LeaderboardActions.UseHelper({
        projectId: __PROJECT_NAME__,
        puzzleName: this.configPuzzleName,
        puzzleId: this.puzzleID,
        helperUsed: usedHelper,
      })
    );
  }

  public canPauseSilently(): boolean {
    if (!this.puzzleFrameworkConfig.leaderboardEnabled) {
      return true;
    }

    if (!this.leaderboardAssociation) {
      return false;
    }

    if (
      (this.leaderboardAssociation.helpersUsed || []).length > 0 ||
      !this.leaderboardAssociation.wantLeaderboardRules
    ) {
      this.useHelper('pause');
      return true;
    }
    return false;
  }

  public async alertError(error) {
    if (!error) {
      return;
    }

    if (this.errorVisible) {
      return;
    }

    this.errorVisible = true;

    const translated = await Promise.all([
      firstValueFrom(this.translate.get(`PUZZLE.leaderboard_service.error.title`)),
      firstValueFrom(this.translate.get(`PUZZLE.leaderboard_service.error.ok`)),
      firstValueFrom(this.translate.get(`PUZZLE.leaderboard_service.error.err_${error.message}`)),
      firstValueFrom(this.translate.get(`PUZZLE.leaderboard_service.error.err_no_message`)),
    ]);

    let errorMessageTranslation = translated[2];

    if (errorMessageTranslation.startsWith('PUZZLE.')) {
      const unknownErrorTranslation = await firstValueFrom(
        this.translate.get(`PUZZLE.leaderboard_service.error.err_unknown`)
      );
      errorMessageTranslation = `${unknownErrorTranslation} ${error.message}`;
    }

    const alert = await this.alertController.create({
      header: translated[0],
      message: (error.message && errorMessageTranslation) || translated[3],
      backdropDismiss: true,
      cssClass: 'puzzle-alert',
      buttons: [
        {
          text: translated[1],
          cssClass: 'primary',
          role: 'continue',
        },
      ],
    });

    await alert.present();

    await alert.onDidDismiss();

    this.errorVisible = false;

    return false;
  }

  public async willDisableLeaderboardRulesDialog(requestedHelper: string) {
    if (this.puzzleGame.finished) {
      return true;
    }

    if (!this.puzzleFrameworkConfig.leaderboardEnabled) {
      return true;
    }

    if (this.leaderboardAssociation && this.leaderboardAssociation.submittedEntryUUID) {
      return true;
    }

    if (this.leaderboardRulesDialogVisible) {
      return false;
    }

    // there's a race condition shortly after loading and a blur event/pause
    if (!this.leaderboardAssociation) {
      return;
    }

    // do not bother if user has already used ~~this~~ any kind of helper
    // if (this.leaderboardAssociation.helpersUsed.indexOf(requestedHelper) > -1) {
    if ((this.leaderboardAssociation.helpersUsed || []).length > 0) {
      await this.useHelper(requestedHelper);
      return true;
    }

    // do not bother if user hasn't indicated they want to play the leaderboard
    if (!this.leaderboardAssociation.wantLeaderboardRules) {
      await this.useHelper(requestedHelper);
      return true;
    }

    this.leaderboardRulesDialogVisible = true;

    const translated = await Promise.all([
      firstValueFrom(this.translate.get(`PUZZLE.leaderboard_service.rules_alert_${requestedHelper}.title`)),
      firstValueFrom(this.translate.get(`PUZZLE.leaderboard_service.rules_alert_${requestedHelper}.message`)),
      firstValueFrom(this.translate.get(`PUZZLE.leaderboard_service.rules_alert_${requestedHelper}.button_abort`)),
      firstValueFrom(this.translate.get(`PUZZLE.leaderboard_service.rules_alert_${requestedHelper}.button_continue`)),
    ]);

    const alert = await this.alertController.create({
      header: translated[0],
      message: translated[1],
      backdropDismiss: true,
      cssClass: 'puzzle-alert',
      buttons: [
        {
          text: translated[2],
          cssClass: 'primary',
          role: 'cancel',
        },
        {
          text: translated[3],
          cssClass: 'primary',
          role: 'continue',
        },
      ],
    });

    await alert.present();

    const result = await alert.onDidDismiss();

    this.leaderboardRulesDialogVisible = false;

    if (result.role === 'continue') {
      this.useHelper(requestedHelper);
      return true;
    }

    return false;
  }

  async resetSettingsToLeaderboardRules() {
    this.store
      .selectOnce((state) => state.puzzleGameState)
      .subscribe(async (puzzleGameState) => {
        if (
          [LetterOverwriteRestrictions.CorrectLetters, LetterOverwriteRestrictions.CorrectWords].includes(
            puzzleGameState.letterOverwriteRestriction
          )
        ) {
          this.store.dispatch(
            new PuzzleGameActions.SetLetterOverwriteRestriction({
              letterOverwriteRestriction: LetterOverwriteRestrictions.All,
            })
          );
        } else {
          this.store.dispatch(
            new PuzzleGameActions.SetLetterOverwriteRestriction({
              letterOverwriteRestriction: LetterOverwriteRestrictions.None,
            })
          );
        }

        if (
          [LetterDeleteRestrictions.CorrectLetters, LetterDeleteRestrictions.CorrectWords].includes(
            puzzleGameState.letterDeleteRestriction
          )
        ) {
          this.store.dispatch(
            new PuzzleGameActions.SetLetterDeleteRestriction({
              letterDeleteRestriction: LetterDeleteRestrictions.CompletedWords,
            })
          );
        } else {
          this.store.dispatch(
            new PuzzleGameActions.SetLetterDeleteRestriction({ letterDeleteRestriction: LetterDeleteRestrictions.None })
          );
        }

        if (
          [WordJumpOnRestrictions.CorrectWord, WordJumpOnRestrictions.CompleteWord].includes(
            puzzleGameState.wordJumpOnRestriction
          ) &&
          this.framework.wordJumpOnDefaultBasedOnDevice()
        ) {
          this.store.dispatch(
            new PuzzleGameActions.SetWordJumpOnRestriction({
              wordJumpOnRestriction: WordJumpOnRestrictions.CompleteWord,
            })
          );
        } else {
          this.store.dispatch(
            new PuzzleGameActions.SetWordJumpOnRestriction({ wordJumpOnRestriction: WordJumpOnRestrictions.None })
          );
        }

        if (
          [WordJumpToRestrictions.IncompleteWord, WordJumpToRestrictions.IncorrectWord].includes(
            puzzleGameState.wordJumpToRestriction
          )
        ) {
          this.store.dispatch(
            new PuzzleGameActions.SetWordJumpToRestriction({
              wordJumpToRestriction: WordJumpToRestrictions.IncompleteWord,
            })
          );
        } else {
          this.store.dispatch(
            new PuzzleGameActions.SetWordJumpToRestriction({ wordJumpToRestriction: WordJumpToRestrictions.Any })
          );
        }

        this.store.dispatch(
          new PuzzleGameActions.SetHighlightRightAndWrongEnabled({ highlightRightAndWrongEnabled: false })
        );

        this.store.dispatch(new PuzzleGameActions.SetShowErrorsImmediately({ showErrorsImmediately: false }));
      });
  }

  async initDefaultSettings(reset = false) {
    this.store.dispatch(
      new PuzzleGameActions.InitDefaultValuesFromConfig({
        autoZoomEnabled: this.helpers.isMobileDevice()
          ? this.puzzleFrameworkConfig.autoZoomEnabledDefaultMobile
          : this.puzzleFrameworkConfig.autoZoomEnabledDefaultDesktop,
        definitionBarEnabled: this.helpers.isMobileDevice()
          ? this.puzzleFrameworkConfig.definitionBarEnabledDefaultMobile
          : this.puzzleFrameworkConfig.definitionBarEnabledDefaultDesktop,
        keepKeyboardOpen: this.puzzleFrameworkConfig.keepKeyboardOpen,
        nativeKeyboardEnabled: this.puzzleFrameworkConfig.nativeKeyboard,
        highlightRightAndWrongEnabled: false,
        showErrorsImmediately: false,
        reset,
        wordJumpOnRestriction: this.framework.wordJumpOnDefaultBasedOnDevice()
          ? WordJumpOnRestrictions.CompleteWord
          : WordJumpOnRestrictions.None,
        wordJumpToRestriction: WordJumpToRestrictions.IncompleteWord,
      })
    );

    if (reset) {
      this.store.dispatch(new PuzzleGameActions.ResetLetterSkipSettingsForLeaderboard());
    }
  }

  public getPuzzleLeaderboardSubmissionStartDate() {
    let puzzleDate = DateTime.fromJSDate(this.framework.getCurrentDate());

    // convert date to leaderboard timezone, so further calculations reference
    // correctly for the origin player base
    if (this.puzzleFrameworkConfig.leaderboardTimezone) {
      puzzleDate = puzzleDate.setZone(this.puzzleFrameworkConfig.leaderboardTimezone);
    }

    // check if we have a weekly released puzzle
    if (this.puzzleFrameworkConfig.weekStartOffsetFromMonday) {
      // set the moment to no-locale day 0 === moment
      const puzzleWeekStart = getWeekStart(puzzleDate);

      // add the offset from the config
      const puzzleOffsetWeekStart = puzzleWeekStart.plus({
        days: this.puzzleFrameworkConfig.weekStartOffsetFromMonday,
      });

      puzzleDate = puzzleOffsetWeekStart;
    }

    const puzzleDateMidnight = puzzleDate.startOf('day');
    return puzzleDateMidnight;
  }

  public getPuzzleLeaderboardSubmissionEndDate() {
    const submissionStartDate = this.getPuzzleLeaderboardSubmissionStartDate();
    return submissionStartDate.plus({
      seconds: this.puzzleFrameworkConfig.leaderboardSubmissionSeconds || 0,
    });
  }

  public async promptReviewModal() {
    if (this.reviewModalIsActive) {
      return;
    }

    this.reviewModalIsActive = true;

    this.reviewModal = await this.ionicModal.create({
      component: LeaderboardReviewComponent,
      backdropDismiss: true,
      cssClass: ['puzzle-modal', 'puzzle-modal-smaller'],
    });

    this.framework.pauseCurrentGame();
    this.keyboard.disable();

    await this.reviewModal.present();

    const review = await this.reviewModal.onDidDismiss();

    this.keyboard.enable();
    this.framework.resumeCurrentGame();
    this.reviewModalIsActive = false;
  }
}
